import { useQuery } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import AddBox from '@mui/icons-material/AddBox';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Clear from '@mui/icons-material/Clear';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import ViewIcon from '@mui/icons-material/OpenInBrowser';
import Remove from '@mui/icons-material/Remove';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Search from '@mui/icons-material/Search';
import ViewColumn from '@mui/icons-material/ViewColumn';
import dayjs from 'dayjs';
import MaterialTable from '@material-table/core';
import { useRouter } from 'next/router';
import React, { forwardRef, useEffect, useState } from 'react';
import { GET_PRIVATE_POSTS } from '../../../helpers/graphql/posts';
import { getUser } from '../../../helpers/token';
import ChainDetailIcon from '../../Grid/ChainDetailIcon';
import FirstPostCard from '../FirstPostCard';
import DeleteDialog from './DeleteDialog';
import Title from './Title';
import VisibilityIndicator from './VisibilityIndicator';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  View: forwardRef((props, ref) => <ViewIcon {...props} ref={ref} />),
};

tableIcons.Add.displayName = 'Add';
tableIcons.Check.displayName = 'Check';
tableIcons.Clear.displayName = 'Clear';
tableIcons.Delete.displayName = 'Delete';
tableIcons.DetailPanel.displayName = 'DetailPanel';
tableIcons.Edit.displayName = 'Edit';
tableIcons.Export.displayName = 'Export';
tableIcons.Filter.displayName = 'Filter';
tableIcons.FirstPage.displayName = 'FirstPage';
tableIcons.LastPage.displayName = 'LastPage';
tableIcons.NextPage.displayName = 'NextPage';
tableIcons.PreviousPage.displayName = 'PreviousPage';
tableIcons.ResetSearch.displayName = 'ResetSearch';
tableIcons.Search.displayName = 'Search';
tableIcons.SortArrow.displayName = 'SortArrow';
tableIcons.ThirdStateCheck.displayName = 'ThirdStateCheck';
tableIcons.ViewColumn.displayName = 'ViewColumn';
tableIcons.View.displayName = 'View';

const Posts = () => {
  const [deleteOpen, setDeleteOpen] = useState();

  const router = useRouter();

  const handleEdit = permlink => {
    router.push(`/dashboard/publish?permlink=${permlink}`);
  };

  const handleView = permlink => {
    router.push(`/@${getUser()}/${permlink}`);
  };

  const handleDelete = permlink => {
    setDeleteOpen(permlink);
  };

  const [posts, setPosts] = useState();
  const [fullLoaded, setFullLoaded] = useState(false);

  const handleNewPosts = postData => {
    const newPosts = [];
    postData.forEach(post => {
      const {
        title,
        permlink,
        excerpt,
        thumbnail,
        blacklist,
        hiveAuthor,
        hivePerm,
        steemAuthor,
        steemPerm,
        evaluation,
        comments,
        totalSmiles,
        views,
      } = post;
      const updatedAt = dayjs(post.updatedAt);
      newPosts.push({
        title,
        permlink,
        excerpt,
        thumbnail,
        updatedAt,
        blacklist,
        hiveAuthor,
        hivePerm,
        steemAuthor,
        steemPerm,
        evaluation,
        comments: comments || 0,
        totalSmiles: totalSmiles || 0,
        views: views || 0,
      });
    });
    setPosts(newPosts);
  };

  const handleCompleted = postData => {
    if (
      !postData ||
      !postData.privatePosts ||
      !postData.privatePosts.length === 0
    )
      return;
    // Initially fetch 10 posts, then fetch the rest
    handleNewPosts(postData.privatePosts);
    if (!fullLoaded && postData.privatePosts.length === 10) {
      setFullLoaded(true);
      // eslint-disable-next-line no-use-before-define
      if (fetchMore)
        fetchMore({
          variables: {
            offset: postData.privatePosts.length,
            limit: 500,
          },
          // eslint-disable-next-line consistent-return
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return prev;
            }
            handleNewPosts([
              ...prev.privatePosts,
              ...fetchMoreResult.privatePosts,
            ]);
          },
        });
    }
  };

  const { fetchMore, refetch } = useQuery(GET_PRIVATE_POSTS, {
    variables: { limit: 10 },
    // eslint-disable-next-line no-use-before-define
    onCompleted: handleCompleted,
    ssr: false,
  });

  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, []);

  if (!posts)
    return (
      <Grid container spacing={0} alignItems="center" justifyContent="center">
        <Grid item lg={12} md={12} sm={12} xs={12} key="loader">
          <div className="p-5 text-center">
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );

  if (posts.length === 0)
    return (
      <div className="container d-flex justify-content-center mt-5 mb-5">
        <div style={{ maxWidth: '800px' }}>
          <FirstPostCard />
        </div>
      </div>
    );

  return (
    <>
      <div style={{ maxWidth: '100%' }}>
        <MaterialTable
          icons={tableIcons}
          columns={[
            {
              title: 'Post',
              field: 'title',
              render: rowData => (
                <Title
                  title={rowData.title}
                  thumbnail={rowData.thumbnail}
                  excerpt={rowData.excerpt}
                />
              ),
            },
            {
              title: 'Last edit',
              field: 'updatedAt',
              type: 'numeric',
              render: rowData => `${rowData.updatedAt.format('MMMM DD, YYYY')}`,
            },
            {
              title: 'Visibility',
              field: 'visibility',
              render: rowData => (
                <VisibilityIndicator
                  blacklist={rowData.blacklist}
                  permlink={rowData.permlink}
                />
              ),
            },
            {
              title: 'Hive',
              field: 'hive',
              render: rowData => (
                <>
                  <ChainDetailIcon
                    chain="hive"
                    author={rowData.hiveAuthor}
                    permlink={rowData.hivePerm}
                  />
                  {/* <ChainDetailIcon
                    chain="steem"
                    author={rowData.steemAuthor}
                    permlink={rowData.steemPerm}
                  /> */}
                </>
              ),
            },
            {
              title: 'Views',
              field: 'views',
              type: 'numeric',
              customFilterAndSearch: (term, rowData) =>
                Number(rowData.score) > term,
            },
            {
              title: 'Comments',
              field: 'comments',
              type: 'numeric',
              customFilterAndSearch: (term, rowData) =>
                Number(rowData.score) > term,
            },
            {
              title: 'Smiles',
              field: 'totalSmiles',
              type: 'numeric',
              customFilterAndSearch: (term, rowData) =>
                Number(rowData.score) > term,
            },
          ]}
          data={posts}
          title="Posts"
          actions={[
            {
              icon: tableIcons.View,
              tooltip: 'View post',
              onClick: (event, rowData) => handleView(rowData.permlink),
            },
            {
              icon: tableIcons.Edit,
              tooltip: 'Edit post',
              onClick: (event, rowData) => handleEdit(rowData.permlink),
            },
            {
              icon: tableIcons.Delete,
              tooltip: 'Delete Forever',
              onClick: (event, rowData) => handleDelete(rowData.permlink),
            },
          ]}
          options={{ pageSize: 10, actionsColumnIndex: -1 }}
        />
      </div>
      <DeleteDialog
        btnText="Delete Post Forever"
        dialogText="If you delete this post, it will be gone forever and cannot be restored. Deleting the post will not delete it from third-party platforms if you used cross-posting."
        setOpen={setDeleteOpen}
        permlink={deleteOpen}
        setPosts={setPosts}
        posts={posts}
      />
    </>
  );
};

export default Posts;
